import { ChatKit as Chat, useChatKit } from '@openai/chatkit-react'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import { twJoin, twMerge } from 'tailwind-merge'
import {
  ChatKitStarIcon,
  ChatKitStarFilledIcon,
  ChatKitArrowIcon,
  ChatKitArrowFilledIcon,
} from './icons'
import { useChatKitActions } from './hooks'
import useMediaQuery from 'shared/hooks/use-media-query'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { LoadingIcon } from '@systemeio/ui/icons'

export const ChatKit = () => {
  const [isShown, setIsShown] = useState(false)
  const [isRendered, setIsRendered] = useState(false)
  const [isChatReady, setIsChatReady] = useState(false)

  const { t } = useLocoTranslation()

  const { isMD } = useMediaQuery()

  const { getClientSecret, escalateSupportRequest } = useChatKitActions()

  const { control } = useChatKit({
    disclaimer: {
      text: t('dashboard.chat_kit.disclaimer'),
    },
    theme: {
      color: {
        accent: {
          primary: 'hsla(236, 82%, 64%, 1)',
          level: 1,
        },
      },
    },
    startScreen: {
      greeting: t('dashboard.chat_kit.greeting'),
      prompts: [
        {
          icon: 'circle-question',
          label: t('dashboard.chat_kit.prompt_create_funnel'),
          prompt: t('dashboard.chat_kit.prompt_create_funnel'),
        },
        {
          icon: 'circle-question',
          label: t('dashboard.chat_kit.prompt_import_contacts'),
          prompt: t('dashboard.chat_kit.prompt_import_contacts'),
        },
        {
          icon: 'circle-question',
          label: t('dashboard.chat_kit.prompt_create_website'),
          prompt: t('dashboard.chat_kit.prompt_create_website'),
        },
        {
          icon: 'circle-question',
          label: t('dashboard.chat_kit.prompt_send_newsletter'),
          prompt: t('dashboard.chat_kit.prompt_send_newsletter'),
        },
      ],
    },
    api: {
      async getClientSecret() {
        const { clientSecret } = await getClientSecret()

        return clientSecret
      },
    },
    onClientTool: async (invocation: { name: string; params: Record<string, unknown> }) => {
      if (invocation.name === 'create_support_request') {
        try {
          await escalateSupportRequest(invocation.params)

          return { success: true }
        } catch (error) {
          return { success: false }
        }
      }

      return { success: false }
    },
    onReady: () => {
      setIsChatReady(true)
    },
  })

  useEffect(() => {
    const htmlElement = document.documentElement

    if (isShown && !isMD) {
      htmlElement.style.overflow = 'hidden'
    } else {
      htmlElement.style.overflow = ''
    }

    return () => {
      htmlElement.style.overflow = ''
    }
  }, [isShown, isMD])

  const onAIButtonClickHandler = () => {
    if (!isRendered) {
      setIsRendered(true)
      setIsShown(true)
    } else {
      setIsShown(!isShown)
    }
  }

  return (
    <>
      <Script src="https://cdn.platform.openai.com/deployments/chatkit/chatkit.js" async />
      <button
        onClick={onAIButtonClickHandler}
        className={twMerge(
          ` from-6.36% via-23.9% to-70.14% group fixed right-2 bottom-16 z-50 flex h-12 w-12 rounded-full bg-gradient-to-br from-[#4EB2F4] via-[#4076F9] to-[#7A3ADB] transition transition-all hover:scale-110`,
          isShown && !isChatReady && 'animate-pulse',
          isMD ? 'bottom-16' : 'bottom-32',
        )}
        disabled={isShown && !isChatReady}
      >
        {!isShown && (
          <>
            <ChatKitStarIcon
              style={{ width: 28 }}
              className={twMerge(
                'absolute left-1/2 bottom-1/2 -translate-x-1/2 translate-y-1/2 opacity-100 transition-all duration-300 ease-in-out group-hover:opacity-0',
              )}
            />
            <ChatKitStarFilledIcon
              style={{ width: 40 }}
              className={twMerge(
                'absolute left-1/2 bottom-1/2 -translate-x-1/2 translate-y-1/2 opacity-0 transition-all duration-300 ease-in-out group-hover:opacity-100',
              )}
            />
          </>
        )}
        {isShown && !isChatReady && (
          <span
            className={twMerge(
              'absolute left-2/3 bottom-1/2 -translate-x-1/2 translate-y-1/2 opacity-100 transition-all duration-300 ease-in-out group-hover:opacity-0',
            )}
          >
            <LoadingIcon style={{ width: 24, color: 'white' }} />
          </span>
        )}
        {isShown && isChatReady && (
          <>
            <ChatKitArrowIcon
              style={{ width: 28 }}
              className={twMerge(
                'absolute left-1/2 bottom-1/2 -translate-x-1/2 translate-y-1/2 opacity-100 transition-all duration-300 ease-in-out group-hover:opacity-0',
              )}
            />
            <ChatKitArrowFilledIcon
              style={{ width: 40 }}
              className={twMerge(
                'absolute left-1/2 bottom-1/2 -translate-x-1/2 translate-y-1/2 opacity-0 transition-all duration-300 ease-in-out group-hover:opacity-100',
              )}
            />
          </>
        )}
      </button>
      {isRendered && (
        <div
          className={twJoin(
            'z-49 md:shadow-around-sm fixed h-full w-full transition-all duration-300 md:h-[600px] md:w-[320px] md:overflow-hidden md:rounded-md',
            isMD ? 'right-10 bottom-14' : 'left-0 bottom-0 right-0 top-0',
            isShown && isChatReady
              ? 'scale-100 opacity-100'
              : 'pointer-events-none scale-0 opacity-0',
          )}
        >
          <Chat control={control} />
        </div>
      )}
    </>
  )
}
